import { useQueryClient } from "@tanstack/react-query";

/**
 * Hook to get the last sync/update time for a specific query.
 * Useful for showing "Last synced: 08:45" in the UI.
 */
export function useLastSyncTime(queryKey: string, userId?: string): Date | null {
  const queryClient = useQueryClient();
  
  // Build the full query key (matching how hooks use them)
  const fullKey = userId ? [queryKey, userId] : [queryKey];
  const state = queryClient.getQueryState(fullKey);
  
  return state?.dataUpdatedAt 
    ? new Date(state.dataUpdatedAt) 
    : null;
}

/**
 * Get the most recent sync time across multiple queries.
 * Useful for showing a single "Last synced" timestamp.
 */
export function useLatestSyncTime(queryKeys: readonly string[], userId?: string): Date | null {
  const queryClient = useQueryClient();
  
  let latestTime: number | null = null;
  
  for (const key of queryKeys) {
    const fullKey = userId ? [key, userId] : [key];
    const state = queryClient.getQueryState(fullKey);
    
    if (state?.dataUpdatedAt) {
      if (latestTime === null || state.dataUpdatedAt > latestTime) {
        latestTime = state.dataUpdatedAt;
      }
    }
  }
  
  return latestTime ? new Date(latestTime) : null;
}
